import React from 'react';
import type { Highlight } from './Label';
import BulkFieldCheckbox from '../../changeAll/BulkFieldCheckbox';
import { useChangeAll } from '../../changeAll/ChangeAllContext';

export type FlagValue = 'Y' | 'N' | 'P';

type Props = {
  label: string;
  value?: FlagValue;
  onChange?: (value: FlagValue) => void;
  highlight?: Highlight;
  /** Width of the inline label in pixels. Default 56px. */
  labelWidth?: number;
  className?: string;
  /** Renders as a non-interactive value chip — matches drawer parity for
   * order-level stamps that aren't editable. */
  readOnly?: boolean;
  /** When set and change-all mode is enabled, renders an "apply to all pros"
   * checkbox before the label. No effect when change-all mode is off. */
  bulkFieldName?: string;
  /** Optional content rendered after the value chip, inside the highlighted
   * row (e.g. a "Preadvised" tag next to a green Haz flag). */
  suffix?: React.ReactNode;
};

const flagColor = (v: FlagValue) =>
  v === 'Y'
    ? 'text-green-700 dark:text-green-400'
    : v === 'N'
      ? 'text-slate-600 dark:text-gray-300'
      : 'text-amber-600 dark:text-amber-400';

/**
 * Specialised Y/N/P flag selector used densely on the legacy CMS screen.
 * Renders as a small select with single-letter values and color-coded text.
 */
const FlagInput: React.FC<Props> = ({
  label,
  value,
  onChange,
  highlight,
  labelWidth = 56,
  className,
  readOnly,
  bulkFieldName,
  suffix,
}) => {
  const { enabled } = useChangeAll();
  // Fully controlled (matches the fields.tsx FlagInput): the displayed value
  // always tracks the `value` prop. An earlier version kept optimistic local
  // state, but that stranded the select on the user's pick when the parent
  // REJECTED the change (e.g. the LC drop/live hard-block) — leaving the
  // dropdown out of sync with the underlying field. Deriving from `value`
  // means a rejected change simply reverts on the next render.
  const v: FlagValue = value ?? 'N';

  // Yellow / green use the same bright token as the cutoff-date highlight
  // (bright fill + black text); red keeps its softer wash. Green marks an
  // export hazmat that's been pre-advised (feature/#33245).
  const isYellow = highlight === 'yellow';
  const isGreen = highlight === 'green';
  // Both bright fills need black text for contrast (label + value chip).
  const darkText = isYellow || isGreen;
  const wrapHl = isYellow
    ? 'bg-yellow-300 dark:bg-yellow-300 text-black'
    : isGreen
      ? 'bg-green-300 dark:bg-green-300 text-black'
      : highlight === 'red'
        ? 'bg-red-100 dark:bg-red-900/40'
        : '';

  return (
    <div
      className={
        `flex items-center gap-1 py-0.5 rounded-sm min-h-[1.625rem] ${wrapHl} ${className ?? ''}`
      }
    >
      {/* `readOnly` IS the permission answer here — the same prop that turns the
          flag into a static chip. A flag the user cannot set must not offer to set it
          on every pro in the set. */}
      {enabled && bulkFieldName ? (
        <BulkFieldCheckbox fieldName={bulkFieldName} editable={!readOnly} />
      ) : null}
      <span
        className={
          'text-[0.71875rem] font-medium text-right ' +
          (darkText ? 'text-black' : 'text-slate-600 dark:text-gray-300')
        }
        style={{ width: `${labelWidth / 16}rem` }}
      >
        {label}:
      </span>
      {readOnly ? (
        <span
          aria-label={label}
          className={
            'text-[0.75rem] font-semibold ' +
            'px-0.5 py-px h-[1.375rem] w-9 inline-flex items-center justify-center ' +
            (darkText ? 'text-black' : flagColor(v))
          }
        >
          {v}
        </span>
      ) : (
        <select
          aria-label={label}
          value={v}
          onChange={(e) => onChange?.(e.target.value as FlagValue)}
          className={
            'bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-700 ' +
            'rounded text-[0.75rem] font-semibold ' +
            'px-0.5 py-px h-[1.375rem] w-9 text-center cursor-pointer ' +
            flagColor(v)
          }
        >
          {/* Editable options are Y/N only — matches the convention of order
              boolean fields (isTank, hazmat, etc.). The readonly path above
              still renders amber 'P' if the saved value is 'P', so legacy
              data displays correctly even though it can't be re-selected. */}
          <option value="Y">Y</option>
          <option value="N">N</option>
        </select>
      )}
      {suffix}
    </div>
  );
};

export default FlagInput;
